import React, { useMemo, useState } from 'react';

const COLORS = ['#ebedf0', '#c6e48b', '#7bc96f', '#239a3b', '#196127'];

const getColor = (count) => {
    if (count === 0) return COLORS[0];
    if (count === 1) return COLORS[1];
    if (count === 2) return COLORS[2];
    if (count <= 4) return COLORS[3];
    return COLORS[4];
};

const TeamHeatmap = ({ teamMembers, assessmentsStore, weeks = 12 }) => {
    const [tooltip, setTooltip] = useState(null);

    const { days, memberData } = useMemo(() => {
        const now = new Date();
        now.setUTCHours(0, 0, 0, 0);

        // Build array of days (UTC-based to match assessment timestamps)
        const totalDays = weeks * 7;
        const days = [];
        for (let i = totalDays - 1; i >= 0; i--) {
            const d = new Date(now);
            d.setUTCDate(d.getUTCDate() - i);
            days.push(d);
        }

        // Build per-member per-day counts
        const memberData = teamMembers.map(member => {
            const dayCounts = {};
            try {
                const assessments = assessmentsStore.closedAssessmentsForUser(member.id) || [];
                assessments.forEach(a => {
                    if (a.completedDateTime) {
                        const key = new Date(a.completedDateTime).toISOString().split('T')[0];
                        dayCounts[key] = (dayCounts[key] || 0) + 1;
                    }
                });
            } catch (e) { /* skip */ }
            return { id: member.id, name: member.name, dayCounts };
        });

        return { days, memberData };
    }, [teamMembers, assessmentsStore, weeks]);

    const cellSize = 14;
    const cellGap = 2;
    const nameWidth = 120;

    // Month labels
    const monthLabels = useMemo(() => {
        const labels = [];
        let lastMonth = -1;
        days.forEach((d, i) => {
            if (d.getUTCMonth() !== lastMonth) {
                lastMonth = d.getUTCMonth();
                labels.push({ index: i, label: d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' }) });
            }
        });
        return labels;
    }, [days]);

    if (memberData.length === 0) {
        return (
            <div style={{ padding: '30px', textAlign: 'center', color: '#999', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                <div style={{ fontSize: '14px', fontFamily: 'Montserrat' }}>No team members to display.</div>
            </div>
        );
    }

    return (
        <div style={{ position: 'relative' }}>
            {/* Month headers */}
            <div style={{ display: 'flex', marginLeft: nameWidth, marginBottom: '4px' }}>
                {monthLabels.map((m, i) => (
                    <div key={i} style={{
                        position: 'absolute',
                        left: nameWidth + m.index * (cellSize + cellGap),
                        fontSize: '10px',
                        fontFamily: 'Montserrat',
                        color: '#888'
                    }}>
                        {m.label}
                    </div>
                ))}
            </div>

            <div style={{ marginTop: '18px', overflowX: 'auto' }}>
                {memberData.map(member => (
                    <div key={member.id} style={{ display: 'flex', alignItems: 'center', marginBottom: cellGap }}>
                        <div style={{
                            width: nameWidth,
                            minWidth: nameWidth,
                            fontSize: '11px',
                            fontFamily: 'Montserrat',
                            color: '#2c3e50',
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            paddingRight: '8px'
                        }}>
                            {member.name}
                        </div>
                        <div style={{ display: 'flex', gap: `${cellGap}px` }}>
                            {days.map((day, i) => {
                                const key = day.toISOString().split('T')[0];
                                const count = member.dayCounts[key] || 0;
                                return (
                                    <div
                                        key={i}
                                        style={{
                                            width: cellSize,
                                            height: cellSize,
                                            backgroundColor: getColor(count),
                                            borderRadius: '2px',
                                            cursor: count > 0 ? 'pointer' : 'default'
                                        }}
                                        onMouseEnter={(e) => {
                                            const rect = e.target.getBoundingClientRect();
                                            setTooltip({
                                                x: rect.left + rect.width / 2,
                                                y: rect.top - 8,
                                                text: `${member.name}: ${count} practice${count !== 1 ? 's' : ''} on ${day.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}`
                                            });
                                        }}
                                        onMouseLeave={() => setTooltip(null)}
                                    />
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '12px', marginLeft: nameWidth }}>
                <span style={{ fontSize: '10px', fontFamily: 'Montserrat', color: '#888', marginRight: '4px' }}>Less</span>
                {COLORS.map((c, i) => (
                    <div key={i} style={{ width: cellSize, height: cellSize, backgroundColor: c, borderRadius: '2px' }} />
                ))}
                <span style={{ fontSize: '10px', fontFamily: 'Montserrat', color: '#888', marginLeft: '4px' }}>More</span>
            </div>

            {/* Tooltip */}
            {tooltip && (
                <div style={{
                    position: 'fixed',
                    left: tooltip.x,
                    top: tooltip.y,
                    transform: 'translate(-50%, -100%)',
                    backgroundColor: '#2c3e50',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontFamily: 'Montserrat',
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                    zIndex: 1000
                }}>
                    {tooltip.text}
                </div>
            )}
        </div>
    );
};

export default TeamHeatmap;
